<template>
  <div class="table-row settings-tender-table-columns">
    <div class="settings-users-table__cell">
      <UserInfo
        v-if="member.user"
        :key="member.user.id"
        :user="member.user"
        :show-position="false"
      />
    </div>
    <div class="settings-users-table__cell vertical-centering">
      <span class="text-sm file-title">{{ member.user.position }}</span>
    </div>
    <div class="settings-users-table__cell vertical-centering">
      <div v-if="member.role" class="d-contents">
        <v-menu v-if="editableCheck && isActivated" offset-y transition="slide-y-transition">
          <template v-slot:activator="{ on, attrs }">
            <div v-bind="attrs" v-on="on">
              <Badge color="light-grey" class="pointer" bordered>
                <span class="capitalize spacex-4">
                  <span>{{ actualRole }}</span>
                  <img src="/images/chevron-down.svg" />
                </span>
              </Badge>
            </div>
          </template>
          <div class="dropdown-body">
            <DropdownMenuElement @click="changeUserRoleHandler">
              {{ $t('Change to') }} {{ roles[roleForChange.slug] }}
              <template slot="icon">
                <ArrowIcon
                  class="icon-18px"
                  :class="{
                    'turn-left-90': roleForChange.isBetterRole,
                    'turn-right-90': !roleForChange.isBetterRole,
                  }"
                />
              </template>
            </DropdownMenuElement>
            <div class="dropdown-divider" />
            <DropdownMenuElement @click="deleteUserHandler">
              <span class="danger-600">{{ $t('Delete user') }}</span>
              <template slot="icon">
                <CrossIcon class="icon-18px icon-d-600" />
              </template>
            </DropdownMenuElement>
          </div>
        </v-menu>
        <div v-else class="flex-row flex-centering spacex-8">
          <Badge
            color="light-grey"
            :class="{ pointer: editableCheck }"
            bordered
            :muted="!isActivated"
            @click.native="resendInviteHandler"
          >
            <span
              class="capitalize"
              :class="{
                pointer: invitedComputedInfo.activeLink,
                primary: invitedComputedInfo.activeLink,
              }"
            >
              {{ !isActivated ? invitedComputedInfo.text : actualRole }}
            </span>
          </Badge>
          <CrossIcon
            v-if="!isActivated && canI('dismissInvitation')"
            class="pointer"
            @click="deleteUserHandler"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import UserInfo from '@/components/ui/UserInfo';
  import Badge from '@/components/ui/Badge';
  import canI from '@/utils/roles/access';
  import { VMenu } from 'vuetify/lib';
  import DropdownMenuElement from '@/components/ui/dropdown-menu/DropdownMenuElement';
  import CrossIcon from '@/assets/icons/cross-rounded.svg';
  import ArrowIcon from '@/assets/icons/arrow.svg';
  import factory from '@/utils/api/factory';
  import { mapActions, mapState } from 'vuex';

  export default {
    name: 'UserInfoTableRow',
    components: {
      Badge,
      UserInfo,
      VMenu,
      DropdownMenuElement,
      CrossIcon,
      ArrowIcon,
    },
    props: {
      member: {
        type: Object,
        required: true,
      },
    },
    data() {
      return {
        updatedRoleValue: null,
        canResendInvite: true,
      };
    },
    computed: {
      ...mapState({
        companyId: (state) => state.auth?.user?.membership?.id,
        roles: (state) => state.global.roles,
      }),
      editableCheck() {
        let ask;
        switch (this.member?.role) {
          case 'owner':
            ask = 'editOwners';
            break;
          case 'admin':
            ask = 'editAdmins';
            break;
          case 'regular':
            ask = 'editRegulars';
        }

        return canI(ask);
      },
      // Данное поле учитывает значение пришедшее от родителя и
      // обновляется при локально пройденном успешном запросе changeUserRoleHandler
      actualRole() {
        return this.roles[this.updatedRoleValue || this.member?.role];
      },
      isActivated() {
        return this.member?.user?.is_activated;
      },
      roleForChange() {
        if (this.actualRole === 'admin' || (!this.actualRole && this.member?.role === 'admin')) {
          return { slug: 'regular', isBetterRole: false };
        } else if (
          this.actualRole === 'regular' ||
          (!this.actualRole && this.member?.role === 'regular')
        ) {
          return { slug: 'admin', isBetterRole: true };
        }
        return { slug: 'regular', isBetterRole: false };
      },
      invitedComputedInfo() {
        if (this.editableCheck) {
          if (this.canResendInvite) {
            return { text: this.$t('resend invite'), activeLink: true };
          } else {
            return { text: this.$t('invite resent'), activeLink: false };
          }
        } else {
          return { text: this.$t('invited'), activeLink: false };
        }
      },
    },
    methods: {
      ...mapActions({
        confirm: 'confirmModal/openModal',
      }),
      canI,
      async changeUserRoleHandler() {
        const roleForRequest = this.roleForChange;
        try {
          const { data } = await factory(
            `teams/${this.companyId}/members/${this.member?.user?.id}`
          ).customPut({
            role: roleForRequest.slug,
          });
          this.updatedRoleValue = data.role;
        } catch (e) {
          // eslint-disable-next-line no-console
          console.error(e);
        }
      },
      async deleteUserHandler() {
        this.confirm({
          title: this.$t('Are you sure you want to delete this user?'),
          actionText: this.$t('Delete user'),
          cancelText: this.$t('Return'),
          description: this.$t(
            'We will save all their actions and files, they will no longer be able to use Fixtender'
          ),
          style: 'danger',
          action: async () => {
            try {
              await factory(`teams`).deleteInDeepPath(
                this.companyId,
                'members',
                this.member?.user?.id
              );
              this.$emit('reload');
            } catch (e) {
              // eslint-disable-next-line no-console
              console.error(e);
            }
            await this.closeModal();
          },
        });
      },
      resendInviteHandler() {
        if (this.editableCheck && this.canResendInvite) {
          this.$emit('resend-invite', this.member.user.id);
          this.canResendInvite = false;
        }
      },
    },
  };
</script>
